import React from 'react';

type SystemStatus = 'active' | 'busy' | 'offline';

interface TopBarProps {
  totalCost?: string;
  onToggleHistory: () => void;
  isHistoryOpen: boolean;
  historyCount?: number;
  historyLoading?: boolean;
  queueCount?: number;
  systemStatus?: SystemStatus;
}

const STATUS_CONFIG: Record<SystemStatus, { label: string; color: string }> = {
  active: { label: 'Actief', color: 'bg-green-500' },
  busy: { label: 'Bezet', color: 'bg-yellow-500' },
  offline: { label: 'Offline', color: 'bg-red-500' },
};

const sanitizeCost = (value?: string): string => {
  if (!value) return '0.00';
  const cleaned = value.trim().replace(/^[^\d-]+/, '');
  return cleaned.length > 0 ? cleaned : '0.00';
};

export const TopBar: React.FC<TopBarProps> = ({
  totalCost,
  onToggleHistory,
  isHistoryOpen,
  historyCount = 0,
  historyLoading = false,
  queueCount = 0,
  systemStatus = 'active',
}) => {
  const displayCost = sanitizeCost(totalCost);
  const currentStatus = STATUS_CONFIG[systemStatus] ?? STATUS_CONFIG.active;

  return (
    <header className="bg-[var(--color-surface)] border-b border-[var(--color-border)] px-6 py-4 shadow-sm">
      <div className="flex items-center justify-between min-w-0">
        <div className="logo flex-shrink-0">
          <img
            src="/TheorieFoutloos.png"
            alt="Theorie Foutloos"
            className="h-14 md:h-16 w-auto object-contain drop-shadow-sm"
          />
        </div>

        <div className="flex items-center gap-4 flex-shrink-0">
          <div className="cost-display">
            €{displayCost}
          </div>

          <div className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
            <span className={`inline-flex h-2 w-2 rounded-full ${currentStatus.color}`} aria-hidden="true" />
            <span>{currentStatus.label}</span>
          </div>

          <div className="text-sm text-[var(--color-text-secondary)]">
            Queue: {queueCount} jobs
          </div>

          <button
            className="p-2 hover:bg-[var(--color-muted)] rounded-lg transition-colors"
            type="button"
            aria-label="Instellingen"
          >
            <svg className="w-5 h-5 text-[var(--color-text-primary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </button>

          <button
            type="button"
            onClick={onToggleHistory}
            className="inline-flex items-center gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2 text-sm font-medium text-[var(--color-text-primary)] shadow-sm hover:border-[var(--color-primary)] transition-colors disabled:cursor-not-allowed disabled:opacity-60"
            disabled={historyLoading && historyCount === 0}
            aria-expanded={isHistoryOpen}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="hidden sm:inline">Geschiedenis</span>
            {historyCount > 0 && !historyLoading && (
              <span className="ml-1 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-[var(--color-muted)] px-2 text-xs font-semibold text-[var(--color-primary)]">
                {historyCount}
              </span>
            )}
          </button>
        </div>
      </div>
    </header>
  );
};

export default TopBar;
